import { useState, useEffect } from 'react';
import { apiGet } from '../api';

/**
 * useScope — Hierarchical access scope hook used by all CRM/HR list pages.
 *
 * Returns: {
 *   scope:            'mine' | 'team' | 'all'  — current selected scope
 *   setScope:         fn(next)
 *   meta:             { is_manager, can_see_all, team_size } | null
 * }
 *
 * Defaults:
 *   super_admin / MD       → 'all'
 *   any other manager      → 'team'
 *   non-managers           → 'mine'
 *
 * The scope is persisted to localStorage per entity so the user's pick survives
 * page reloads.
 */
export function useScope(entityKey = 'global') {
  const [meta, setMeta] = useState(null);
  const [scope, setScopeState] = useState(() => {
    try {
      return window.localStorage.getItem(`scope:${entityKey}`) || null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    apiGet('/employees/am-i-manager')
      .then((m) => {
        setMeta(m);
        // Initialise scope only if not already set from localStorage
        setScopeState((cur) => {
          if (cur) return cur;
          if (m?.can_see_all) return 'all';
          if (m?.is_manager) return 'team';
          return 'mine';
        });
      })
      .catch(() => {
        setMeta({ is_manager: false, can_see_all: false });
        setScopeState((cur) => cur || 'mine');
      });
  }, []);

  const setScope = (next) => {
    setScopeState(next);
    try { window.localStorage.setItem(`scope:${entityKey}`, next); } catch {}
  };

  return { scope, setScope, meta };
}
